import { FunctionComponent, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import type { OnlineOperationalModifierOption } from '@/libs/helpers/adapters'
import { useMenuItem, MAX_NESTED_MODS_DEPTH } from '@/libs/helpers/hooks/useMenuItem'
import type { CartChildItem } from '@/libs/helpers/utils'
import { CheckBox, Grid, QuantitySelector, SvgIcon, Text } from '@/tbui'

interface ModifierOptionProps {
  // Used to differentiate which modifier option is selected if there are
  // multiple modifier groups
  selectionId: string
  modifierOption: OnlineOperationalModifierOption
  maxSelectionReached: boolean
  modifiersUpdated: () => void
  selectionMax: number
  selectionMin: number
  // API expects modifierGroupXRefID but we use "entityID" here for consistency
  modifierGroupID: string
  hasNestedModifiers: boolean
  modifierGroupDOMRefs: React.MutableRefObject<Record<string, HTMLDivElement>>
}

const StyledQuantitySelector = styled(QuantitySelector)`
  justify-content: center;
  @media (max-width: ${(props) => props.theme.breakpoints.XS}) {
    gap: 8px;
  }
  i {
    font-size: 30px;
    height: 30px;
    width: 30px;
  }
  p {
    font-size: 16px;
    height: 18px;
  }
  gap: 10px;
`

const StyledCheckbox = styled(CheckBox)`
  i {
    display: block;
  }
`

const StyledGrid = styled(Grid)<{ hasError?: boolean }>`
  padding: 12px 0;
  &:not(:last-child) {
    border-bottom: 2px solid ${({ theme }) => theme.palette.GRAY_1};
  }
  background: ${(props) => (props.hasError ? props.theme.palette.ERROR_LIGHT : 'transparent')};
`

const StyledNestModsGrid = styled(Grid)<{ hasNestedModifiers: boolean }>`
  cursor: ${(props) => (props.hasNestedModifiers ? 'pointer' : 'unset')};
`

const ModifierOption: FunctionComponent<ModifierOptionProps> = ({
  modifierOption,
  maxSelectionReached,
  modifiersUpdated,
  selectionMax,
  modifierGroupID,
  hasNestedModifiers,
  selectionMin,
}) => {
  const { t } = useTranslation()
  const isCheckboxVariant = selectionMax === 1 && selectionMin === 1

  const {
    modifiers,
    openNestedModifier,
    currentErrors,
    currentModifiers,
    updateQuantity,
    shouldDisplayErrors,
    openNestedModifiersTrace,
  } = useMenuItem()

  const selectedModifier = (() =>
    currentModifiers.find(
      (modifier) =>
        modifier.itemID === modifierOption.XRefID &&
        modifier.modifierGroupXRefID === modifierGroupID
    ))()
  const sQuantity = selectedModifier?.quantity
  const quantity = sQuantity != null ? Number(sQuantity) : 0

  const hasError = (() => {
    if (!shouldDisplayErrors) {
      return false
    }
    const node = currentErrors[modifierGroupID]
    return (
      node != null &&
      node.error === 'NESTED_MODIFIER' &&
      node.childItems &&
      !!node.childItems[modifierOption.XRefID]
    )
  })()

  const price = modifierOption.modifierGroupRefs[modifierGroupID]?.price ?? modifierOption.price

  const handleQuantityChange = (newQuantity: number): void => {
    let otherOptionsToRemove: {
      cartChildItem: Omit<CartChildItem, 'quantity'>
      quantity: number
    }[] = []
    if (isCheckboxVariant) {
      otherOptionsToRemove = currentModifiers
        .filter(
          (mod) =>
            mod.modifierGroupXRefID === modifierGroupID && mod.itemID !== modifierOption.XRefID
        )
        .map((mod) => ({ cartChildItem: mod, quantity: 0 }))
    }
    updateQuantity([
      ...otherOptionsToRemove,
      {
        cartChildItem: {
          price,
          itemID: modifierOption.XRefID,
          modifierGroupXRefID: modifierGroupID,
        },
        quantity: newQuantity,
      },
    ])
  }

  const prevModifiersRef = useRef<CartChildItem[] | undefined>(undefined)
  useEffect(() => {
    // Skip on first render: only notify when `modifiers` changes after mount.
    if (prevModifiersRef.current !== undefined && prevModifiersRef.current !== modifiers) {
      modifiersUpdated()
    }
    prevModifiersRef.current = modifiers
  }, [modifiers, modifiersUpdated])

  const key = `${modifierGroupID}_${modifierOption.XRefID}`

  // openNestedModifiersTrace does not include the current open modifier, so we check for length >= MAX_NESTED_MODS_DEPTH - 1
  const hasReachedMaximumDepth = openNestedModifiersTrace.length >= MAX_NESTED_MODS_DEPTH - 1
  const showOutOfStock = modifierOption.isOutOfStock || modifierOption.canBeOrdered === false

  const renderInput =
    quantity > 0 && !isCheckboxVariant ? (
      <StyledQuantitySelector
        quantityMin={0}
        quantityMax={selectionMax === 0 ? 99 : selectionMax}
        initialQuantity={quantity}
        onChange={(newQuantity) => handleQuantityChange(newQuantity)}
        disableAdd={maxSelectionReached || showOutOfStock}
      />
    ) : (
      <StyledCheckbox
        isSelected={quantity > 0}
        onChange={(e) => handleQuantityChange(e.target.checked ? 1 : 0)}
        name={key}
        id={key}
        variant={isCheckboxVariant ? 'CHECK' : 'ADD'}
        isDisabled={showOutOfStock || (maxSelectionReached && !isCheckboxVariant)}
        iconSize="30px"
      />
    )

  return (
    <StyledGrid template="1fr auto" gap="6px" align="end" hasError={hasError}>
      <Grid
        template="auto 1fr"
        gap="16px"
        align="center"
        data-test={`modifier-option-container-${key}`}
      >
        {renderInput}
        <Grid gap="6px">
          <Text
            type="H4"
            transform="capitalize"
            style={showOutOfStock ? { textDecoration: 'line-through' } : {}}
            data-test={`modifier-option-${key}-label`}
          >
            {modifierOption.label}
          </Text>
        </Grid>
      </Grid>
      <StyledNestModsGrid
        template="1fr auto"
        gap="6px"
        align="end"
        hasNestedModifiers={hasNestedModifiers && !hasReachedMaximumDepth}
        data-test={`modifier-option-container-${key}-nested-mods`}
        onClick={() => {
          if (!hasNestedModifiers || hasReachedMaximumDepth) {
            return
          }
          if (
            quantity === 0 &&
            !modifierOption.isOutOfStock &&
            modifierOption.canBeOrdered !== false
          ) {
            handleQuantityChange(1)
          }
          openNestedModifier({
            itemID: modifierOption.XRefID,
            modifierGroupXRefID: modifierGroupID,
          })
        }}
      >
        <Text color="TEXT_2" data-test={`modifier-option-${key}-price`}>
          {t('currency', { value: price })}
        </Text>
        {hasNestedModifiers && !hasReachedMaximumDepth && (
          <SvgIcon name="chevron_right" fontSize="20px" color="PRIMARY" />
        )}
      </StyledNestModsGrid>
    </StyledGrid>
  )
}

export default ModifierOption
